import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API_BASE, formatApiError, safeBlobDownload } from "@/lib/api";
import { formatInr } from "@/lib/currency";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, DownloadSimple, PaintBrush, ArrowsClockwise, Eye, CheckCircle, XCircle, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";

function tokenHeaders() {
  const t = localStorage.getItem("aig_access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function PartiesPage() {
  const [parties, setParties] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/parties", { params: q ? { q } : {} });
    setParties(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Issuers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            One template + one unique logo per issuer, deterministic and permanent.
            All invoices are billed to EVOPLUS IT SERVICES PRIVATE LIMITED.
          </p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Search party..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="parties-search" />
          <Button onClick={load} variant="outline">Search</Button>
        </div>
      </div>

      <div className="card-panel">
        <table className="w-full table-dense" data-testid="parties-table">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left">Issuer</th>
              <th className="text-left">PAN</th>
              <th className="text-left">Bank</th>
              <th className="text-left">Template</th>
              <th className="text-right">Invoices</th>
              <th className="text-right">Draft ₹</th>
              <th className="text-right">Approved ₹</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {parties.map((p) => (
              <tr key={p.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`party-row-${p.id}`}>
                <td className="font-medium flex items-center gap-2 py-2">
                  <Users size={16} weight="duotone" />
                  <Link to={`/parties/${p.id}`} className="hover:underline">{p.name}</Link>
                </td>
                <td className="font-mono text-xs">{p.pan || "—"}</td>
                <td className="text-xs">
                  {p.bank_name || "—"}
                  {p.bank_account && <div className="text-muted-foreground font-mono">{p.bank_account}</div>}
                </td>
                <td><span className="beige-tag px-2 py-0.5 rounded text-xs">{p.template_code}</span></td>
                <td className="num-cell">{p.total_invoices}</td>
                <td className="num-cell">{formatInr(p.drafts_total)}</td>
                <td className="num-cell">{formatInr(p.approved_total)}</td>
                <td className="text-right">
                  <Link to={`/parties/${p.id}`} className="text-primary text-sm hover:underline">Open</Link>
                </td>
              </tr>
            ))}
            {parties.length === 0 && (
              <tr><td colSpan="8" className="text-center py-8 text-muted-foreground">No issuers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PartyDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [party, setParty] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [changeTplOpen, setChangeTplOpen] = useState(false);
  const [newTpl, setNewTpl] = useState("");
  const [regenRow, setRegenRow] = useState(null);
  const [prefCount, setPrefCount] = useState(5);
  const [voidingId, setVoidingId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [rejectingIds, setRejectingIds] = useState([]);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`/parties/${id}`);
    setParty(data);
    setNewTpl(data.template_code);
  };
  useEffect(() => { load(); api.get("/templates").then((r) => setTemplates(r.data)); /* eslint-disable-next-line */ }, [id]);

  if (!party) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const drafts = party.invoices.filter((i) => i.status === "draft");
  const approved = party.invoices.filter((i) => i.status === "approved");

  const approveAll = async () => {
    const ids = drafts.map((d) => d.id);
    if (!ids.length) return;
    try {
      await api.post("/invoices/approve", { invoice_ids: ids });
      toast.success("Invoices approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPdf = async (invId) => {
    const res = await fetch(`${API_BASE}/invoices/${invId}/pdf`, { credentials: "include", headers: tokenHeaders() });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const doVoid = async () => {
    if (!voidReason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/invoices/${voidingId}/void`, { reason: voidReason });
      toast.success("Voided");
      setVoidingId(null); setVoidReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post("/invoices/reject", { invoice_ids: rejectingIds, reason: rejectReason });
      toast.success("Rejected");
      setRejectingIds([]); setRejectReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doChangeTemplate = async () => {
    try {
      await api.put(`/parties/${id}/template`, { template_code: newTpl });
      toast.success("Template updated on drafts");
      setChangeTplOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doRegenerate = async () => {
    try {
      await api.post(`/parties/${id}/regenerate`, { imported_row_id: regenRow, preferred_count: prefCount });
      toast.success("Distribution regenerated");
      setRegenRow(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // reconciliation per imported row
  const reconRows = party.imported_rows.map((ir) => {
    const invs = party.invoices.filter((i) => i.imported_row_id === ir.id);
    const imported = Number(ir.total_amount);
    const generated = invs.reduce((s, i) => s + Number(i.amount), 0);
    return { ir, invs, imported, generated, diff: imported - generated };
  });

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground"><Link to="/parties" className="hover:underline">Parties</Link></div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={26} weight="duotone" /> {party.name}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-sm">
            <span className="beige-tag px-2 py-1 rounded text-xs" data-testid="party-template-tag">Template · {party.template_code}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {hasRole(user, "admin") && (
            <Dialog open={changeTplOpen} onOpenChange={setChangeTplOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="change-template-btn"><PaintBrush size={16} className="mr-2" />Change template</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Change template</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Label>Template</Label>
                  <Select value={newTpl} onValueChange={setNewTpl}>
                    <SelectTrigger data-testid="new-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only DRAFT invoices will use the new template. Already-approved PDFs are immutable.
                  </p>
                </div>
                <DialogFooter>
                  <Button onClick={doChangeTemplate} data-testid="confirm-change-template">Update</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {approved.length > 0 && (
            <Button variant="outline" onClick={() => safeBlobDownload(
              `${API_BASE}/invoices/party/${id}/download-zip`,
              `invoices_${party.name}.zip`,
              (m) => toast.error(m),
            )} data-testid="download-party-zip"><DownloadSimple size={16} className="mr-2" /> Download ZIP</Button>
          )}
          {(hasRole(user, "admin", "reviewer") && drafts.length > 0) && (
            <Button onClick={approveAll} data-testid="approve-all-btn"><CheckCircle size={16} className="mr-2" />Approve all drafts</Button>
          )}
        </div>
      </div>

      <div className="card-panel">
        <div className="p-4 border-b border-border font-medium">Reconciliation per import row</div>
        <table className="w-full table-dense">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left">Batch row</th>
              <th className="text-left">Description</th>
              <th className="text-right">Imported</th>
              <th className="text-right">Invoices ({drafts.length + approved.length})</th>
              <th className="text-right">Generated</th>
              <th className="text-right">Difference</th>
              <th className="text-left">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reconRows.map(({ ir, invs, imported, generated, diff }) => {
              const allApproved = invs.length > 0 && invs.every((i) => i.status === "approved");
              const status = allApproved ? "Approved" : diff === 0 ? "Balanced" : "Unbalanced";
              return (
                <tr key={ir.id} className="border-t border-border/60">
                  <td>#{ir.row_number}</td>
                  <td className="max-w-xs truncate">{ir.description}</td>
                  <td className="num-cell">{formatInr(imported)}</td>
                  <td className="num-cell">{invs.length}</td>
                  <td className="num-cell">{formatInr(generated)}</td>
                  <td className={`num-cell ${diff === 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatInr(diff)}</td>
                  <td><StatusBadge status={status} /></td>
                  <td className="text-right">
                    {hasRole(user, "admin") && !allApproved && (
                      <Dialog open={regenRow === ir.id} onOpenChange={(o) => setRegenRow(o ? ir.id : null)}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`regen-btn-${ir.id}`}><ArrowsClockwise size={14} className="mr-1" />Regenerate</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Regenerate distribution</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            <Label>Invoice count (4–7)</Label>
                            <Input type="number" min={4} max={7} value={prefCount} onChange={(e) => setPrefCount(Number(e.target.value))} data-testid="regen-count" />
                            <p className="text-xs text-muted-foreground">This will delete existing draft invoices for this row and generate a new distribution summing exactly to {formatInr(imported)}.</p>
                          </div>
                          <DialogFooter><Button onClick={doRegenerate} data-testid="confirm-regen">Confirm</Button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card-panel">
        <div className="p-4 border-b border-border font-medium">Invoices</div>
        <table className="w-full table-dense" data-testid="party-invoices-table">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left">Invoice #</th>
              <th className="text-left">Seq</th>
              <th className="text-right">Amount</th>
              <th className="text-left">Date</th>
              <th className="text-left">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {party.invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-border/60">
                <td className="font-mono text-xs">{inv.invoice_number}</td>
                <td className="text-xs">{inv.sequence_in_party}/{inv.total_in_party}</td>
                <td className="num-cell">{formatInr(inv.amount)}</td>
                <td className="text-xs">{inv.invoice_date}</td>
                <td><StatusBadge status={inv.status} /></td>
                <td className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openPdf(inv.id)} data-testid={`preview-${inv.id}`}>
                    <Eye size={14} className="mr-1" />PDF
                  </Button>
                  {inv.status === "draft" && hasRole(user, "admin", "reviewer") && (
                    <>
                      <Button size="sm" variant="ghost" onClick={async () => {
                        try { await api.post("/invoices/approve", { invoice_ids: [inv.id] }); toast.success("Approved"); load(); }
                        catch (e) { toast.error(formatApiError(e)); }
                      }} data-testid={`approve-${inv.id}`}><CheckCircle size={14} className="mr-1" />Approve</Button>
                      <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => { setRejectingIds([inv.id]); setRejectReason(""); }} data-testid={`reject-${inv.id}`}><XCircle size={14} className="mr-1" />Reject</Button>
                    </>
                  )}
                  {inv.status === "approved" && hasRole(user, "admin") && (
                    <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => { setVoidingId(inv.id); setVoidReason(""); }} data-testid={`void-${inv.id}`}><Prohibit size={14} className="mr-1" />Void</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Void modal */}
      <Dialog open={!!voidingId} onOpenChange={(o) => !o && setVoidingId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Void invoice</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Textarea rows={4} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="void-reason" />
            <p className="text-xs text-muted-foreground">Voided invoices are retained but marked. Numbers are never reused.</p>
          </div>
          <DialogFooter><Button onClick={doVoid} variant="destructive" data-testid="confirm-void">Void invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={rejectingIds.length > 0} onOpenChange={(o) => !o && setRejectingIds([])}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject draft</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Rejection reason (required)</Label>
            <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
          </div>
          <DialogFooter><Button onClick={doReject} variant="destructive" data-testid="confirm-reject">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
